import React from "react";
import ActionsCardThumbnail from "@/components/admin/thumbnails/ActionsCardThumbnail";
import AlertSettingsThumbnail from "@/components/admin/thumbnails/AlertSettingsThumbnail";
import { BankAccountsCardThumbnail } from "@/components/admin/thumbnails/BankAccountsCardThumbnail";
import BoardReadySummaryCardThumbnail from "@/components/admin/thumbnails/BoardReadySummaryCardThumbnail";
import { CriticalAlertsCardThumbnail } from "@/components/admin/thumbnails/CriticalAlertsCardThumbnail";
import { CurrentCashMiniCardThumbnail } from "@/components/admin/thumbnails/CurrentCashMiniCardThumbnail";
import DataSourcesCardThumbnail from "@/components/admin/thumbnails/DataSourcesCardThumbnail";
import { ExecutiveCashPositionCardThumbnail } from "@/components/admin/thumbnails/ExecutiveCashPositionCardThumbnail";
import GovernanceComplianceCardThumbnail from "@/components/admin/thumbnails/GovernanceComplianceCardThumbnail";
import LastSyncCardThumbnail from "@/components/admin/thumbnails/LastSyncCardThumbnail";
import NinetyDayInteractiveChartCardThumbnail from "@/components/admin/thumbnails/NinetyDayInteractiveChartCardThumbnail";
import OverdueInvoicesMiniCardThumbnail from "@/components/admin/thumbnails/OverdueInvoicesMiniCardThumbnail";
import RecentTransactionsCardThumbnail from "@/components/admin/thumbnails/RecentTransactionsCardThumbnail";
import { RecurringTransactionsCardThumbnail } from "@/components/admin/thumbnails/RecurringTransactionsCardThumbnail";
import ScenarioComparisonCardThumbnail from "@/components/admin/thumbnails/ScenarioComparisonCardThumbnail";
import ThirtyDayForecastMiniCardThumbnail from "@/components/admin/thumbnails/ThirtyDayForecastMiniCardThumbnail";
import UncategorizedItemsCardThumbnail from "@/components/admin/thumbnails/UncategorizedItemsCardThumbnail";
import UpcomingPaymentsMiniCardThumbnail from "@/components/admin/thumbnails/UpcomingPaymentsMiniCardThumbnail";
import VarianceWeekMiniCardThumbnail from "@/components/admin/thumbnails/VarianceWeekMiniCardThumbnail";
import type { ModeType } from "@/types";

// Permission Types - Complete rebuild aligned with actual dashboard cards and features
export type Permission =
  // DATA ACCESS
  | "view_transactions"
  | "export_data"

  // CASH POSITION CARDS
  | "view_current_cash_mini_card"
  | "view_bank_accounts_card"
  | "view_executive_cash_position_card"
  | "view_group_cash_position_card"

  // FORECAST CARDS & CHARTS
  | "view_30day_forecast_mini_card"
  | "view_90day_interactive_chart_card"
  | "view_forecasts"
  | "view_90day_forecast_screen"
  | "view_12month_forecast_screen"
  | "view_3year_forecast_screen"

  // SCENARIO PLANNING
  | "view_scenarios"
  | "view_parked_scenarios"
  | "view_scenario_dependencies"
  | "create_scenarios"
  | "edit_system_scenarios"
  | "view_scenario_comparison_card"
  | "compare_scenarios"
  | "approve_reject_scenarios"

  // TRANSACTION MANAGEMENT
  | "view_recent_transactions_card"
  | "view_upcoming_recurring_transactions_card"
  | "edit_transaction_categories"
  | "manage_recurring_transactions"

  // DATA QUALITY & RECONCILIATION
  | "view_uncategorized_items_card"

  // INVOICES & PAYMENTS
  | "view_overdue_invoices_mini_card"
  | "view_upcoming_payments_mini_card"
  | "view_actions_card"
  | "create_actions"
  | "complete_actions"

  // VARIANCE & ANALYSIS
  | "view_variance_card"
  | "view_variance_analysis_screen"
  | "view_variance_by_category"
  | "manage_forecast_snapshots" // Create point-in-time forecast snapshots for variance analysis
  | "delete_forecast_snapshots" // Archive (soft-delete) forecast snapshots — separate from create, destructive act
  | "trigger_forecast_refresh" // Manually initiate a full forecast refresh outside of the scheduled sync

  // STRATEGIC & BOARD (CFO Features)
  | "view_board_ready_summary_card"
  | "view_governance_compliance_card"

  // BOARD & EXTERNAL (External User Features)
  | "view_critical_alerts_card"
  | "configure_alert_settings"

  // REPORTS
  // Three-permission model — report catalogue configuration lives inside the Reports page itself
  | "view_reports" // Access the Reports page and run any assigned reports
  | "manage_report_permissions" // Configure which reports are visible to which roles (Manage tab in Reports)
  | "manage_custom_reports" // Build, edit, and publish custom reports (Release 2 builder)

  // ADMINISTRATION
  | "manage_users"
  | "configure_data_sources"

  // SYSTEM ADMINISTRATION (System Admin Only)
  | "view_data_sources_card"
  | "view_last_sync_card"
  | "view_recent_activity_feed"
  | "manage_general_settings"
  | "manage_terminology"
  | "manage_dashboard_layout"
  | "manage_roles_permissions"
  | "manage_user_defaults"
  | "manage_approval_workflows"
  | "manage_bank_account_details" // Finance-user permission: edit facility limits, restrictions per account

  // AI RECOMMENDATIONS
  // No standalone page permission — menu appears if user has ANY of these child permissions
  | "view_operational_insights" // Operational recommendations: payment timing, supplier terms, clustering
  | "view_strategic_insights" // Strategic recommendations: concentration risk, seasonal patterns, anomalies
  | "create_action_from_recommendation" // "Create Action" button visible on recommendations
  | "dismiss_recommendation"; // "Mark as Reviewed" button visible on recommendations

/**
 * Onboarding is available to Evo Administrators, or to any user holding all of these
 * permissions — one per onboarding step.
 */
export const ONBOARDING_REQUIRED_PERMISSIONS: Permission[] = [
  "manage_general_settings",
  "configure_data_sources",
  "manage_roles_permissions",
  "manage_users",
  "manage_dashboard_layout",
];

export type EntityRestriction = "all" | "specific" | "own";
export type DataAccessLevel = "full" | "consolidated";
export type DisplayPrecision = "full" | "rounded" | "abbreviated" | "millions";

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: Permission[];
  entityRestriction: EntityRestriction;
  allowedEntities?: string[]; // Only used when entityRestriction is 'specific'
  dataAccessLevel: DataAccessLevel; // Controls detail level: full drill-down or consolidated totals only
  displayPrecision: DisplayPrecision; // Controls how financial amounts are displayed
  allowUserOverride: boolean; // Whether user can change their own display precision
  userCount: number;
  isTemplate: boolean;
  isLocked?: boolean; // System roles that cannot be edited or deleted
  isExternalRole?: boolean; // External user role - view-only (all Edit permissions disabled)
  isExternalRoleLocked?: boolean; // Whether the external role flag can be changed
}

export interface PermissionItem {
  id: Permission;
  label: string;
  description?: string;
  demoOnly?: boolean;
  subPermissions?: PermissionItem[]; // Nested sub-permissions
  thumbnail?: React.ComponentType<{
    isHovered?: boolean;
    alwaysVisible?: boolean;
  }>; // Thumbnail component for visual preview
}

export interface PermissionGroup {
  id: string;
  name: string;
  permissions: PermissionItem[];
  demoOnly?: boolean;
}

// Permission Groups for UI - Reorganized to match navigation structure
export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    id: "data_access",
    name: "DATA ACCESS",
    permissions: [
      {
        id: "export_data",
        label: "Export data",
        description: "Export data from lists and tables (CSV, Excel)",
      },
    ],
  },
  {
    id: "dashboard",
    name: "1. DASHBOARD",
    permissions: [
      {
        id: "view_current_cash_mini_card",
        label: "View current cash mini card",
        description:
          "Dashboard card showing current cash balance with coverage",
        thumbnail: CurrentCashMiniCardThumbnail,
      },
      {
        id: "view_30day_forecast_mini_card",
        label: "View 30-day forecast mini card",
        description: "Dashboard card showing 30-day projection",
        thumbnail: ThirtyDayForecastMiniCardThumbnail,
      },
      {
        id: "view_overdue_invoices_mini_card",
        label: "View overdue invoices mini card",
        description: "Dashboard card showing overdue invoice count and total",
        thumbnail: OverdueInvoicesMiniCardThumbnail,
      },
      {
        id: "view_upcoming_payments_mini_card",
        label: "View upcoming payments mini card",
        description: "Dashboard card showing upcoming payment obligations",
        thumbnail: UpcomingPaymentsMiniCardThumbnail,
      },
      {
        id: "view_variance_card",
        label: "View variance card",
        description: "Dashboard card showing forecast vs actual variance",
        thumbnail: VarianceWeekMiniCardThumbnail,
      },
      {
        id: "view_uncategorized_items_card",
        label: "View uncategorised items card",
        description:
          "Dashboard card showing transactions needing categorisation",
        thumbnail: UncategorizedItemsCardThumbnail,
      },
      {
        id: "view_bank_accounts_card",
        label: "View bank accounts breakdown card",
        description: "Dashboard card showing individual account balances",
        thumbnail: BankAccountsCardThumbnail,
      },
      {
        id: "view_upcoming_recurring_transactions_card",
        label: "View upcoming recurring commitments card",
        description: "Dashboard card showing scheduled recurring items",
        thumbnail: RecurringTransactionsCardThumbnail,
      },
      {
        id: "view_actions_card",
        label: "View actions card",
        description:
          "Dashboard card showing tasks assigned to you and tasks you have delegated",
        thumbnail: ActionsCardThumbnail,
      },
      {
        id: "view_critical_alerts_card",
        label: "View critical alerts card",
        description: "Dashboard card monitoring thresholds and recent alerts",
        thumbnail: CriticalAlertsCardThumbnail,
      },
      {
        id: "view_governance_compliance_card",
        label: "View governance & compliance card",
        description:
          "Dashboard card showing statutory payments status, audit readiness, upcoming deadlines",
        thumbnail: GovernanceComplianceCardThumbnail,
      },
      {
        id: "view_scenario_comparison_card",
        label: "View scenario comparison card",
        description: "Dashboard card showing side-by-side scenario metrics",
        thumbnail: ScenarioComparisonCardThumbnail,
      },
      {
        id: "view_executive_cash_position_card",
        label: "View executive cash position card",
        description:
          "CEO mega-card with all key metrics. Note: designed to be the only card on a dashboard.",
        thumbnail: ExecutiveCashPositionCardThumbnail,
      },
      {
        id: "view_recent_transactions_card",
        label: "View recent transactions card",
        description:
          "Dashboard card showing recent transaction list with filters",
        thumbnail: RecentTransactionsCardThumbnail,
      },
      {
        id: "view_90day_interactive_chart_card",
        label: "View 90-day interactive forecast chart",
        description:
          "Dashboard chart with colour-coded zones and daily drill-down",
        thumbnail: NinetyDayInteractiveChartCardThumbnail,
      },
      {
        id: "view_board_ready_summary_card",
        label: "View board-ready summary card",
        description:
          "Dashboard card with liquidity ratio, days cash on hand, cash conversion cycle",
        thumbnail: BoardReadySummaryCardThumbnail,
      },
      {
        id: "configure_alert_settings",
        label: "Configure alert settings",
        description: "Customise alert thresholds and notification preferences",
        thumbnail: AlertSettingsThumbnail,
      },
    ],
  },
  {
    id: "forecasts",
    name: "2. FORECASTS",
    permissions: [
      {
        id: "view_forecasts",
        label: "View forecasts",
        description: "Access to forecast timelines and projections",
      },
      {
        id: "view_90day_forecast_screen",
        label: "View 90-day forecast screen",
        description: "Access to 90-day forecast timeline page",
      },
      {
        id: "view_12month_forecast_screen",
        label: "View 12-month forecast screen",
        description: "Access to 12-month forecast page",
      },
      {
        id: "view_3year_forecast_screen",
        label: "View 3-year forecast screen",
        description: "Access to 3-year strategic forecast page",
        demoOnly: true,
        subPermissions: [],
      },
      {
        id: "trigger_forecast_refresh",
        label: "Trigger forecast refresh",
        description:
          "Manually initiate a full forecast refresh outside of the scheduled sync",
      },
    ],
  },
  {
    id: "transactions",
    name: "3. TRANSACTIONS",
    permissions: [
      {
        id: "view_transactions",
        label: "View transactions",
        description: "Access transaction lists",
      },
      {
        id: "edit_transaction_categories",
        label: "Edit transaction categories",
        description: "Change transaction categorisation",
      },
      {
        id: "manage_recurring_transactions",
        label: "Manage recurring commitments",
        description: "Manage recurring commitment templates",
      },
    ],
  },
  {
    id: "scenarios",
    name: "4. SCENARIOS",
    permissions: [
      {
        id: "view_scenarios",
        label: "View scenarios",
        description: "See your own + shared scenarios + system defaults",
        subPermissions: [
          {
            id: "view_parked_scenarios",
            label: "View parked scenarios",
            description: "Access Parked tab for inactive/archived scenarios",
          },
          {
            id: "view_scenario_dependencies",
            label: "View scenario dependencies",
            description:
              "Access Dependencies tab showing scenario relationships",
          },
        ],
      },
      {
        id: "create_scenarios",
        label: "Create scenarios",
        description: "Build new scenarios (includes edit/share/delete of own)",
      },
      {
        id: "edit_system_scenarios",
        label: "Edit system scenarios",
        description: "Modify Conservative/Optimistic default scenario settings",
      },
      {
        id: "compare_scenarios",
        label: "Compare scenarios",
        description: "Access comparison tools and charts",
      },
      {
        id: "approve_reject_scenarios",
        label: "Approve/reject scenarios",
        description: "Formal approval workflow",
      },
    ],
  },
  {
    id: "variance_analysis",
    name: "5. VARIANCE ANALYSIS",
    demoOnly: true,
    permissions: [
      {
        id: "view_variance_analysis_screen",
        label: "View variance analysis screen",
        description: "Access variance analysis section",
        subPermissions: [
          {
            id: "view_variance_by_category",
            label: "View variance by category breakdown",
            description: "See detailed category-level variance comparisons",
          },

          {
            id: "manage_forecast_snapshots",
            label: "Take forecast snapshots",
            description:
              "Create point-in-time forecast snapshots for variance analysis",
          },
          {
            id: "delete_forecast_snapshots",
            label: "Archive forecast snapshots",
            description:
              "Archive (soft-delete) forecast snapshots — separate permission as this is a destructive, irreversible act",
          },
        ],
      },
    ],
  },
  {
    id: "ai_recommendations",
    name: "6. AI RECOMMENDATIONS",
    demoOnly: true,
    permissions: [
      {
        id: "view_operational_insights",
        label: "View operational insights",
        description:
          "See operational recommendations: payment timing optimisation, supplier terms opportunities, cash clustering risks. Menu item appears automatically when this or strategic insights is granted.",
      },
      {
        id: "view_strategic_insights",
        label: "View strategic insights",
        description:
          "See strategic recommendations: cash concentration risk, seasonal pattern warnings, anomaly detection. Menu item appears automatically when this or operational insights is granted.",
      },
      {
        id: "create_action_from_recommendation",
        label: "Create action from recommendation",
        description:
          'Shows the "Create Action" button on recommendations — creates a task in the Actions page directly from an insight',
      },
      {
        id: "dismiss_recommendation",
        label: "Dismiss / mark as reviewed",
        description:
          'Shows the "Mark as Reviewed" button — allows the user to dismiss a recommendation from their view',
      },
    ],
  },
  {
    id: "actions",
    name: "7. ACTIONS",
    permissions: [
      {
        id: "create_actions",
        label: "Create and assign actions",
        description: "Create tasks and assign them to other users",
      },
      {
        id: "complete_actions",
        label: "Complete actions",
        description:
          "Mark assigned tasks as complete with a completion comment",
      },
    ],
  },
  {
    id: "reports",
    name: "8. REPORTS",
    demoOnly: true,
    permissions: [
      {
        id: "view_reports",
        label: "View reports",
        description:
          "Access the Reports page and run any reports they have been given access to. Which reports appear is configured within the Reports page itself.",
      },
      {
        id: "manage_report_permissions",
        label: "Manage report permissions",
        description:
          "Access the Manage tab within Reports — configure which reports are visible to which roles and users. Does not require view_reports.",
      },
      {
        id: "manage_custom_reports",
        label: "Manage custom reports",
        description:
          "Build, edit, and publish custom reports (Release 2). Grants access to the custom report builder within the Manage tab.",
      },
    ],
  },
  {
    id: "admin_dashboard",
    name: "9. ADMIN DASHBOARD",
    demoOnly: true,
    permissions: [
      {
        id: "view_data_sources_card",
        label: "View data sources card",
        description: "Monitor connected data sources status",
        thumbnail: DataSourcesCardThumbnail,
      },
      {
        id: "view_last_sync_card",
        label: "View last sync card",
        description: "Monitor data synchronisation status",
        thumbnail: LastSyncCardThumbnail,
      },
      {
        id: "view_recent_activity_feed",
        label: "View recent activity feed",
        description: "System activity log on admin dashboard",
      },
    ],
  },
  {
    id: "user_management",
    name: "10. USER MANAGEMENT",
    permissions: [
      {
        id: "manage_users",
        label: "Manage users",
        description: "User account management and role assignments",
      },
      {
        id: "manage_roles_permissions",
        label: "Manage roles & permissions",
        description: "Configure user roles",
      },
      {
        id: "manage_user_defaults",
        label: "Manage user defaults",
        description: "Set default preferences",
        demoOnly: true,
      },
      {
        id: "manage_approval_workflows",
        label: "Manage approval workflows",
        description:
          "Configure scenario approval workflow stages and routing rules",
      },
    ],
  },
  {
    id: "system_settings",
    name: "11. SYSTEM SETTINGS",
    permissions: [
      {
        id: "manage_general_settings",
        label: "Manage general settings",
        description: "Company info, financial year, defaults",
      },
      {
        id: "manage_terminology",
        label: "Manage terminology",
        description: "Customise system terminology",
      },
      {
        id: "manage_dashboard_layout",
        label: "Manage dashboard layout",
        description: "Configure default card sequence per role",
      },

      {
        id: "configure_data_sources",
        label: "Configure data sources",
        description: "Data integration setup",
      },
    ],
  },
  {
    id: "finance_settings",
    name: "12. FINANCE SETTINGS",
    permissions: [
      {
        id: "manage_bank_account_details",
        label: "Manage bank account details",
        description:
          "Edit overdraft facilities, credit limits, and restricted fund annotations per account",
      },
    ],
  },
];

// Builds the set of permission (and sub-permission) IDs that are visible in the
// given mode. In live mode, `demoOnly` groups, permissions, and sub-permissions
// are excluded — mirroring what the role detail view renders on screen.
export function getVisiblePermissionIds(mode: ModeType): Set<string> {
  const visibleIds = new Set<string>();

  for (const group of PERMISSION_GROUPS) {
    if (mode !== "demo" && group.demoOnly) {
      continue;
    }

    for (const permission of group.permissions) {
      if (mode !== "demo" && permission.demoOnly) {
        continue;
      }

      visibleIds.add(permission.id);

      for (const subPermission of permission.subPermissions ?? []) {
        if (mode !== "demo" && subPermission.demoOnly) {
          continue;
        }
        visibleIds.add(subPermission.id);
      }
    }
  }

  return visibleIds;
}

// Counts how many of the given permission keys are surfaced in the UI for the
// given mode. This is the single source of truth for the permission count shown
// on both the Roles & Permissions list cards and the role detail view.
export function countVisiblePermissions(
  permissionKeys: string[],
  mode: ModeType,
): number {
  const visibleIds = getVisiblePermissionIds(mode);
  return permissionKeys.filter((key) => visibleIds.has(key)).length;
}

// Role Templates - Keep existing roles but with empty permissions (will be configured separately)
export const ROLE_TEMPLATES: Role[] = [
  {
    id: "preparer",
    name: "User",
    description: "Full data entry and categorisation",
    dataAccessLevel: "full",
    displayPrecision: "full",
    allowUserOverride: false,
    permissions: [
      // DATA ACCESS (2/5)
      "view_transactions",
      "export_data",

      // CASH POSITION CARDS (1/5)
      "view_bank_accounts_card",

      // FORECAST CARDS & CHARTS (2/4)
      "view_90day_interactive_chart_card",
      "view_forecasts",

      // FORECASTS - SCREENS
      "view_90day_forecast_screen",
      "view_12month_forecast_screen",
      "view_3year_forecast_screen",

      // SCENARIO PLANNING (3/9 - no sub-permissions)
      "view_scenarios",
      "create_scenarios",
      "compare_scenarios",

      // TRANSACTION MANAGEMENT (3/5)
      "view_recent_transactions_card",
      "edit_transaction_categories",
      "view_upcoming_recurring_transactions_card",

      // DATA QUALITY & RECONCILIATION (1/2)
      "view_uncategorized_items_card",

      // INVOICES & PAYMENTS (2/6)
      "view_overdue_invoices_mini_card",
      "view_upcoming_payments_mini_card",

      // ACTIONS
      "view_actions_card",
      "complete_actions",
    ],
    entityRestriction: "all",
    userCount: 3,
    isTemplate: true,
    isLocked: true,
  },
  {
    id: "manager",
    name: "Finance Manager",
    description: "Review, approve, and analyse forecasts",
    dataAccessLevel: "full",
    displayPrecision: "rounded",
    allowUserOverride: true,
    permissions: [
      // DATA ACCESS (2/3)
      "view_transactions",
      "export_data",

      // CASH POSITION CARDS (2/5)
      "view_current_cash_mini_card",
      "view_uncategorized_items_card",

      // FORECAST CARDS & CHARTS (3/4)
      "view_30day_forecast_mini_card",
      "view_90day_interactive_chart_card",
      "view_forecasts",

      // FORECASTS - SCREENS
      "view_90day_forecast_screen",
      "view_12month_forecast_screen",
      "view_3year_forecast_screen",

      // SCENARIO PLANNING (5/7)
      "view_scenarios",
      "create_scenarios",
      "edit_system_scenarios",
      "compare_scenarios",
      "approve_reject_scenarios",

      // TRANSACTION MANAGEMENT (4/5)
      "view_recent_transactions_card",
      "edit_transaction_categories",
      "view_upcoming_recurring_transactions_card",
      "manage_recurring_transactions",

      // INVOICES & PAYMENTS (2/4)
      "view_overdue_invoices_mini_card",
      "view_upcoming_payments_mini_card",

      // ACTIONS
      "view_actions_card",
      "create_actions",
      "complete_actions",

      // VARIANCE & ANALYSIS (3/10 + 2 sub-permissions)
      "view_variance_card",
      "view_variance_analysis_screen",
      "view_variance_by_category",
      "manage_forecast_snapshots",

      // AI RECOMMENDATIONS — operational + strategic + both action permissions
      "view_operational_insights",
      "view_strategic_insights",
      "create_action_from_recommendation",
      "dismiss_recommendation",

      // REPORTS
      "view_reports",
      "manage_report_permissions",
      "manage_custom_reports",

      // FINANCE SETTINGS
      "manage_bank_account_details",

      // FORECASTING OPERATIONS
      "trigger_forecast_refresh",
    ],
    entityRestriction: "all",
    userCount: 1,
    isTemplate: true,
  },
  {
    id: "regional",
    name: "Regional Manager",
    description: "Multi-location oversight",
    dataAccessLevel: "full",
    displayPrecision: "rounded",
    allowUserOverride: true,
    permissions: [
      // DATA ACCESS (1/3)
      "view_transactions",

      // CASH POSITION CARDS (1/5)
      "view_current_cash_mini_card",
      "view_uncategorized_items_card",

      // FORECAST CARDS & CHARTS (2/4)
      "view_90day_interactive_chart_card",
      "view_forecasts",

      // FORECASTS - SCREENS
      "view_90day_forecast_screen",
      "view_12month_forecast_screen",
      "view_3year_forecast_screen",

      // SCENARIO PLANNING (3/7)
      "view_scenarios",
      "create_scenarios",
      "compare_scenarios",

      // TRANSACTION MANAGEMENT (1/7)
      "view_recent_transactions_card",

      // INVOICES & PAYMENTS (2/6)
      "view_overdue_invoices_mini_card",
      "view_upcoming_payments_mini_card",

      // ACTIONS
      "view_actions_card",
      "create_actions",
      "complete_actions",

      // VARIANCE & ANALYSIS (2/10)
      "view_variance_card",
      "view_variance_analysis_screen",

      // AI RECOMMENDATIONS — strategic only (regional directors see the big picture, not operational detail)
      "view_strategic_insights",
      "create_action_from_recommendation",
      "dismiss_recommendation",

      // REPORTS
      "view_reports",

      // FINANCE SETTINGS
      "manage_bank_account_details",
    ],
    entityRestriction: "specific",
    allowedEntities: [
      "Birmingham",
      "Manchester",
      "Leeds",
      "Sheffield",
      "Liverpool",
      "Newcastle",
      "Nottingham",
      "Derby",
    ],
    userCount: 1,
    isTemplate: true,
  },
  {
    id: "cfo",
    name: "CFO",
    description: "Full strategic access and multi-entity control",
    dataAccessLevel: "full",
    displayPrecision: "full",
    allowUserOverride: true,
    permissions: [
      // DATA ACCESS (2/3)
      "view_transactions",
      "export_data",

      // CASH POSITION CARDS (2/5)
      "view_current_cash_mini_card",
      "view_bank_accounts_card",
      "view_uncategorized_items_card",

      // CFO DASHBOARD CARDS — R2 (view_board_ready_summary_card, view_governance_compliance_card disabled for R1)

      // FORECAST CARDS & CHARTS (2/4)
      "view_30day_forecast_mini_card",
      "view_forecasts",

      // FORECASTS - SCREENS
      "view_90day_forecast_screen",
      "view_12month_forecast_screen",
      "view_3year_forecast_screen",

      // SCENARIO PLANNING (7/7 - ALL)
      "view_scenarios",
      "view_parked_scenarios",
      "view_scenario_dependencies",
      "create_scenarios",
      "edit_system_scenarios",
      "compare_scenarios",
      "approve_reject_scenarios",

      // TRANSACTION MANAGEMENT (4/5)
      "view_recent_transactions_card",
      "edit_transaction_categories",
      "view_upcoming_recurring_transactions_card",
      "manage_recurring_transactions",

      // DATA QUALITY & RECONCILIATION (1/2)
      "view_uncategorized_items_card",

      // INVOICES & PAYMENTS (2/4)
      "view_overdue_invoices_mini_card",
      "view_upcoming_payments_mini_card",

      // ACTIONS
      "view_actions_card",
      "create_actions",
      "complete_actions",

      // VARIANCE & ANALYSIS (3/10 + 2 sub-permissions)
      "view_variance_card",
      "view_variance_analysis_screen",
      "view_variance_by_category",
      "manage_forecast_snapshots",
      "delete_forecast_snapshots",

      // AI RECOMMENDATIONS — full access (CFO sees everything, can act on all recommendations)
      "view_operational_insights",
      "view_strategic_insights",
      "create_action_from_recommendation",
      "dismiss_recommendation",

      // REPORTS
      "view_reports",
      "manage_report_permissions",
      "manage_custom_reports",

      // FINANCE SETTINGS
      "manage_bank_account_details",

      // FORECASTING OPERATIONS
      "trigger_forecast_refresh",
    ],
    entityRestriction: "all",
    userCount: 1,
    isTemplate: true,
  },
  {
    id: "executive",
    name: "CEO",
    description: "Read-only executive summary",
    dataAccessLevel: "consolidated",
    displayPrecision: "abbreviated",
    allowUserOverride: false,
    permissions: [
      // DATA ACCESS (0/5)

      // CASH POSITION CARDS (1/5)
      "view_executive_cash_position_card",

      // FORECAST CARDS & CHARTS (1/4)
      "view_forecasts",

      // FORECASTS - SCREENS (1/3 — 3-Year strategic view only)
      "view_3year_forecast_screen",

      // SCENARIO PLANNING (2/7)
      "view_scenarios",
      "compare_scenarios",

      // AI RECOMMENDATIONS — strategic only, can dismiss but not create actions (read-only exec)
      "view_strategic_insights",
      "dismiss_recommendation",

      // REPORTS
      "view_reports",
    ],
    entityRestriction: "all",
    userCount: 1,
    isTemplate: true,
  },
  {
    id: "system_admin",
    name: "System Administrator",
    description: "Complete system control and configuration",
    dataAccessLevel: "full",
    displayPrecision: "full",
    allowUserOverride: false,
    permissions: [
      // DATA ACCESS (0/5)

      // ADMINISTRATION (4/4 - ALL)
      "manage_users",
      "configure_data_sources",

      // SYSTEM ADMINISTRATION (11/12 - access_admin_dashboard removed, now dynamic based on card permissions)
      "view_data_sources_card",
      "view_last_sync_card",
      "view_recent_activity_feed",
      "manage_general_settings",
      "manage_terminology",
      "manage_dashboard_layout",

      "manage_roles_permissions", // CRITICAL - prevents lockout!
      "manage_user_defaults",
      "manage_approval_workflows",

      // REPORTS — admin manages the report catalogue and can run admin-specific reports
      "view_reports",
      "manage_report_permissions",
      "manage_custom_reports",
    ],
    entityRestriction: "all",
    userCount: 1,
    isTemplate: true,
    isLocked: true,
  },
  {
    id: "external",
    name: "External User",
    description: "View-only stakeholder access",
    dataAccessLevel: "consolidated",
    displayPrecision: "millions",
    allowUserOverride: false,
    permissions: [
      // DATA ACCESS (0/5)

      // CASH POSITION CARDS (0/5)

      // FORECAST CARDS & CHARTS (2/4) - 12-month forecast included
      "view_forecasts",
      "view_12month_forecast_screen",

      // SCENARIO PLANNING (3/7)
      "view_scenarios",
      "view_scenario_comparison_card",
      "compare_scenarios",

      // VARIANCE & ANALYSIS (0/4) - All variance permissions removed

      // BOARD & EXTERNAL — R2 (view_critical_alerts_card, view_board_ready_summary_card disabled for R1)
      "configure_alert_settings",

      // REPORTS
      "view_reports",
    ],
    entityRestriction: "all",
    userCount: 1,
    isTemplate: true,
    isLocked: true,
    isExternalRole: true,
    isExternalRoleLocked: true,
  },
];

// User assignments for demo
export interface UserRoleAssignment {
  userId: string;
  name: string;
  email: string;
  roleId: string;
  entity?: string;
}

export const USER_ROLE_ASSIGNMENTS: UserRoleAssignment[] = [
  {
    userId: "1",
    name: "Catherine Hughes",
    email: "catherine.hughes@buildright.co.uk",
    roleId: "executive",
  },
  {
    userId: "2",
    name: "David Chen",
    email: "david.chen@buildright.co.uk",
    roleId: "system_admin",
  },
  {
    userId: "3",
    name: "Edna Foster",
    email: "edna.foster@buildright.co.uk",
    roleId: "preparer",
    // entity restriction applies via allowedEntities in role definition (R2)
  },
  {
    userId: "4",
    name: "Linda Kowalski",
    email: "linda.kowalski@buildright.co.uk",
    roleId: "cfo",
  },
  {
    userId: "5",
    name: "Marcus Patel",
    email: "marcus.patel@buildright.co.uk",
    roleId: "regional",
  },
  {
    userId: "6",
    name: "Sarah Mitchell",
    email: "sarah.mitchell@buildright.co.uk",
    roleId: "manager",
  },
  {
    userId: "7",
    name: "Emma Thompson",
    email: "emma.thompson@buildright.co.uk",
    roleId: "preparer",
  },
  {
    userId: "8",
    name: "Jennifer O'Sullivan",
    email: "jennifer.osullivan@boardmember.ie",
    roleId: "external",
  },
  {
    userId: "9",
    name: "John Smith",
    email: "john.smith@buildright.co.uk",
    roleId: "preparer",
  },
];
